import { adminRoutes } from '@bettermode/domain/routes/web/admin'
import { useAuthMember } from '@tribeplatform/react-sdk/hooks'

import { useI18n } from '../../i18n/providers/I18nProvider.js'
import { CommandBarHookResult, emptyCommandBarResult } from '../types.js'

export const useCommandBarAdminSettings: CommandBarHookResult = () => {
  const { $t } = useI18n()
  const { isInitialLoading, isAdmin } = useAuthMember()

  const categoryTexts = {
    managePeople: $t({
      id: 'CommandBar.AdminSettings.ManagePeople',
      defaultMessage: 'Manage people',
    }),
    manageContent: $t({
      id: 'CommandBar.AdminSettings.ManageContent',
      defaultMessage: 'Manage content',
    }),
    administration: $t({
      id: 'Generics.Administration',
      defaultMessage: 'Administration',
    }),
    customize: $t({
      id: 'Generics.Customize',
      defaultMessage: 'Customize',
    }),
    analytics: $t({
      id: 'Generics.Analytics',
      defaultMessage: 'Analytics',
    }),
    apps: $t({
      id: 'Generics.Apps',
      defaultMessage: 'Apps',
    }),
  } as const

  if (!isAdmin) {
    return emptyCommandBarResult
  }
  return {
    isLoading: isInitialLoading,
    actions: [
      {
        name: $t({
          id: 'CommandBar.AdminSettings.ManageMembers',
          defaultMessage: 'Manage {MEMBERS}',
        }),
        icon: 'member',
        url: adminRoutes.people.members,
        category: categoryTexts.managePeople,
      },
      {
        name: $t({
          id: 'CommandBar.AdminSettings.ManageStaff',
          defaultMessage: 'Manage staff',
        }),
        icon: 'members',
        url: adminRoutes.people.staff,
        category: categoryTexts.managePeople,
      },
      {
        name: $t({
          id: 'CommandBar.AdminSettings.ManageInvitations',
          defaultMessage: 'Manage invitations',
        }),
        icon: 'inbox',
        url: adminRoutes.people.invitations,
        category: categoryTexts.managePeople,
      },
      {
        name: $t({
          id: 'CommandBar.AdminSettings.ManageProfileFeields',
          defaultMessage: 'Manage profile fields',
        }),
        icon: 'members',
        url: adminRoutes.people.fields,
        category: categoryTexts.managePeople,
      },
      {
        name: $t({
          id: 'Generics.ManageBadges',
          defaultMessage: 'Manage badges',
        }),
        icon: 'badge-check',
        url: adminRoutes.people.badges,
        category: categoryTexts.managePeople,
      },
      {
        name: $t({
          id: 'CommandBar.AdminSettings.ManagePosts',
          defaultMessage: 'Manage posts',
        }),
        icon: 'post',
        url: adminRoutes.content.root,
        category: categoryTexts.manageContent,
      },
      {
        name: $t({
          id: 'CommandBar.AdminSettings.ManageSpaces',
          defaultMessage: 'Manage {SPACES}',
        }),
        icon: 'grid',
        url: adminRoutes.content.spaces,
        category: categoryTexts.manageContent,
      },
      {
        name: $t({
          id: 'CommandBar.AdminSettings.ManageTags',
          defaultMessage: 'Manage {TAGS}',
        }),
        icon: 'tag',
        url: adminRoutes.content.tags,
        category: categoryTexts.manageContent,
      },
      {
        name: $t({
          id: 'CommandBar.AdminSettings.ManageCMSModels',
          defaultMessage: 'Manage CMS models',
        }),
        icon: 'post-type',
        url: adminRoutes.content.cms.root,
        category: categoryTexts.manageContent,
      },
      {
        name: $t({
          id: 'Generics.CustomizeSpaces',
          defaultMessage: 'Manage customize {SPACES}',
        }),
        icon: 'customizer',
        url: adminRoutes.design.customizer.root,
        category: categoryTexts.customize,
      },
      {
        name: $t({
          id: 'Generics.CustomizeThemeColors',
          defaultMessage: 'Customize theme colors',
        }),
        icon: 'appearance',
        url: adminRoutes.design.appearance.colors,
        category: categoryTexts.customize,
      },
      {
        name: $t({
          id: 'Generics.CustomizeCommunityNavigation',
          defaultMessage: 'Customize {COMMUNITY} navigation',
        }),
        icon: 'template',
        url: adminRoutes.design.customizer.navigation,
        category: categoryTexts.customize,
      },
      {
        name: $t({
          id: 'Generics.CustomizeThemeTypography',
          defaultMessage: 'Customize theme typography',
        }),
        icon: 'font-size',
        url: adminRoutes.design.appearance.typography,
        category: categoryTexts.customize,
      },
      {
        name: $t({
          id: 'CommandBar.AdminSettings.CommunitySettings',
          defaultMessage: '{COMMUNITY_CC} settings',
        }),
        icon: 'settings',
        url: adminRoutes.settings.network.root,
        category: categoryTexts.administration,
      },
      {
        name: $t({
          id: 'CommandBar.AdminSettings.CommunityModerationSettings',
          defaultMessage: '{COMMUNITY_CC} moderation settings',
        }),
        icon: 'moderation',
        url: adminRoutes.settings.moderation.root,
        category: categoryTexts.administration,
      },
      {
        name: $t({
          id: 'Generics.Billing',
          defaultMessage: 'Billing',
        }),
        icon: 'credit-card',
        url: adminRoutes.billing.root,
        category: categoryTexts.administration,
      },
      {
        name: $t({
          id: 'CommandBar.AdminSettings.CommunityAnalytics',
          defaultMessage: '{COMMUNITY_CC} analytics',
        }),
        icon: 'analytics',
        url: adminRoutes.reports.analytics.root,
        category: categoryTexts.analytics,
      },
      {
        name: $t({
          id: 'CommandBar.AdminSettings.CommunityAuditLogs',
          defaultMessage: '{COMMUNITY_CC} audit logs',
        }),
        icon: 'audit-log',
        url: adminRoutes.reports.auditLogs,
        category: categoryTexts.analytics,
      },
      {
        name: $t({
          id: 'CommandBar.AdminSettings.InstallApps',
          defaultMessage: 'Install apps',
        }),
        icon: 'apps',
        url: adminRoutes.appStore.apps.root,
        category: categoryTexts.apps,
      },
    ],
  }
}
